import { AUTH_MODE, CART_MODE, CONFIRM_SALE_MODE, PAYMENT_MODE } from '@src/components/Checkout/constants';
import { AUTH_SUB_STEP, type AuthSubStep } from '@src/views/checkout/authSubStep';
import type { CheckoutMode } from '@src/views/checkout/types';

export type CheckoutBackTarget =
  | { type: 'mode'; mode: CheckoutMode }
  | { type: 'authSubStep'; step: AuthSubStep }
  | { type: 'continueShopping' }
  | { type: 'previousPage' }
  | { type: 'none' };

type ResolveCheckoutBackTargetOptions = {
  /** False on a direct link: the page behind the checkout only opens it again, and `/` is not the seller's site. */
  canLeave?: boolean;
};

const LEAVING_TARGETS: ReadonlyArray<CheckoutBackTarget['type']> = ['previousPage', 'continueShopping'];

// Where the checkout cannot be left the cart comes from outside it, so there is no cart step to go back to.
const leavesCheckout = (target: CheckoutBackTarget) =>
  LEAVING_TARGETS.includes(target.type) || (target.type === 'mode' && target.mode === CART_MODE);

export function resolveCheckoutBackTarget(
  mode: CheckoutMode,
  authSubStep: AuthSubStep,
  options: ResolveCheckoutBackTargetOptions = {},
): CheckoutBackTarget {
  const { canLeave = true } = options;
  const target = resolveTarget(mode, authSubStep);

  return !canLeave && leavesCheckout(target) ? { type: 'none' } : target;
}

function resolveTarget(mode: CheckoutMode, authSubStep: AuthSubStep): CheckoutBackTarget {
  if (mode === AUTH_MODE) {
    switch (authSubStep) {
      // The cart step this used to return to was never built, and these flows skip it anyway.
      case AUTH_SUB_STEP.IDENTIFICATION:
        return { type: 'previousPage' };
      case AUTH_SUB_STEP.LOGIN:
      case AUTH_SUB_STEP.SIGNUP:
        return { type: 'authSubStep', step: AUTH_SUB_STEP.IDENTIFICATION };
      case AUTH_SUB_STEP.FORGOT_PASSWORD:
        return { type: 'authSubStep', step: AUTH_SUB_STEP.LOGIN };
      default:
        return { type: 'mode', mode: CART_MODE };
    }
  }

  if (mode === CART_MODE) {
    return { type: 'continueShopping' };
  }

  if (mode === PAYMENT_MODE) {
    return { type: 'continueShopping' };
  }

  if (mode === CONFIRM_SALE_MODE) {
    return { type: 'mode', mode: CART_MODE };
  }

  return { type: 'mode', mode: CART_MODE };
}
